import { RemoveCircle } from '@mui/icons-material';
import type { DataGridProProps as DataGridProps, GridColDef } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRemoveFromRole } from '~/api/roles';
import { ActionButton } from '~/components/button';
import { SeparatedDataGrid } from '~/components/data-grid';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { EmptyState } from '~/components/empty-state';
import { AuthorizedLink } from '~/components/links';
import { ListCheckbox } from '~/components/list-row';
import { mergeGridActionProps, type GridActionCellProps } from '~/components/table';
import { useConfirmDialog } from '~/hooks/dialogs';
import AddEventTypesIllustration from '~/images/illustrations/create-event-type.svg';
import type {
  RoleEventTypeList__Role as Role,
  RoleEventTypeList__EventTypeRule as Rule,
} from '../RoleEventTypeList.generated';

const NoRowsOverlay = () => {
  const { t } = useTranslation();
  return (
    <EmptyState
      illustration={AddEventTypesIllustration}
      description={t('pages.roles.event_types.empty_state.description')}
      header={t('pages.roles.event_types.empty_state.header')}
    />
  );
};

interface RemoveButtonProps extends GridActionCellProps {
  networkRole: Role;
  rule: Rule;
}

const RemoveButton = ({ networkRole, rule, ...cellProps }: RemoveButtonProps) => {
  const { t } = useTranslation();
  const [remove, { called, reset }] = useRemoveFromRole({
    variables: { roleId: networkRole.id, eventTypeRuleIds: rule.id },
  });

  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();

  const onClick = async () => {
    if (!(await confirmDelete())) return;
    await remove();
    reset();
  };

  return (
    <>
      <ActionButton
        color="error"
        disabled={called}
        Icon={RemoveCircle}
        {...mergeGridActionProps({ onClick }, cellProps)}
        title={t('common.actions.remove')}
      />

      <ConfirmDialog
        {...confirmDeleteProps}
        cancel={t('common.buttons.cancel')}
        confirm={t('pages.roles.dialogs.remove_event_type.confirm')}
        deleteConfirm
        prompt={t('pages.roles.dialogs.remove_event_type.prompt', {
          event_type_name: rule.eventType.name,
          role_name: networkRole.name,
        })}
        title={t('pages.roles.dialogs.remove_event_type.title')}
      />
    </>
  );
};

const useColumns = (role: Role): GridColDef<Rule>[] => {
  const { t } = useTranslation();
  return useMemo(
    () => [
      {
        field: 'name',
        headerName: t('common.labels.name'),
        flex: 1,
        valueGetter: (_value, row) => row.eventType.name,
        renderCell: ({ row }) => (
          <AuthorizedLink authorized={row.eventType.canUpdate.value} entity={row.eventType} />
        ),
      },
      {
        field: 'actions',
        type: 'actions',
        getActions: ({ row }) => [<RemoveButton key={0} networkRole={role} rule={row} />],
      },
    ],
    [role, t],
  );
};

export interface EventTypeTableProps extends Omit<DataGridProps<Rule>, 'columns'> {
  loading: boolean;
  role: Role;
}

export const EventTypeTable = ({ loading, role, ...props }: EventTypeTableProps) => {
  const columns = useColumns(role);

  return (
    <SeparatedDataGrid
      columns={columns}
      loading={loading}
      slots={{
        baseCheckbox: ListCheckbox,
        columnResizeIcon: () => null,
        noRowsOverlay: NoRowsOverlay,
      }}
      {...props}
    />
  );
};
